import {environment} from "../../environments/environment";
import {inject, Injectable, Injector, Type} from "@angular/core";
import {UserRepo} from "./api/user.repo";
import {UserDuckDb} from "./duckdb/user.duckdb";
import {notDefined} from "../libs/utils/epic.utils";
import {LoggingService} from "../services/logger/logging.service";
import {InfoRepo} from "./api/info.repo";
import {InfoDuckDb} from "./duckdb/info.duckdb";
import {I18nRepo} from "./api/i18n.repo";
import {I18nDuckDb} from "./duckdb/i18n.duckdb";
import {OrgRepo} from "./api/org.repo";
import {OrgDuckDb} from "./duckdb/org.duckdb";
import {OverviewRepo} from "./api/overview.repo";
import {OverviewDuckDb} from "./duckdb/overview.duckdb";
import {NotificationRepo} from "./api/notification.repo";
import {NotificationDuckDb} from "./duckdb/notification.duckdb";
import {VatRateRepo} from "./api/vat-rate.repo";
import {VatRateDuckDb} from "./duckdb/vat-rate.duckdb";
import {CurrencyDuckDb} from "./duckdb/currency.duckdb";
import {CurrencyRepo} from "./api/currency.repo";
import {InvoiceRepo} from "./api/invoice.repo";
import {InvoiceDuckDb} from "./duckdb/invoice.duckdb";
import {ContactRepo} from "./api/contact.repo";
import {ContactDuckDb} from "./duckdb/contact.duckdb";
import {ReceiptDuckDb} from "./duckdb/receipt.duckdb";
import {ReceiptRepo} from "./api/receipt.repo";
import {ArticleRepo} from "./api/article.repo";
import {ArticleDuckDb} from "./duckdb/article.duckdb";
import {ContactVehicleRepo} from "./api/vehicle/contact-vehicle.repo";
import {ContactVehicleDuckDb} from "./duckdb/contact-vehicle.duckdb";
import {VehicleRepo} from "./api/vehicle/vehicle.repo";
import {VehicleDuckDb} from "./duckdb/vehicle.duckdb";
import {MediaRepo} from "./api/media.repo";
import {MediaDuckDb} from "./duckdb/media.duckdb";
import {ContactRelationDuckdb} from "./duckdb/contact-relation.duckdb";
import {ContactRelationRepo} from "./api/contact-relation.repo";
import {DeathRepo} from "./api/death.repo";
import {DeathDuckDb} from "./duckdb/death.duckdb";
import {RegistryOfficeDuckDb} from "./duckdb/registry-office.repo";
import {RegistryOfficeRepo} from "./api/registry-office.repo";
import {GoodsGroupDuckDb} from "./duckdb/goods-group.duckdb";
import {GoodsGroupRepo} from "./api/goods-group.repo";
import {VehicleHistoryRepo} from "./api/vehicle/vehicle-history.repo";
import {VehicleHistoryDuckDb} from "./duckdb/vehicle-history.duckdb";
import {VehicleRepairRepo} from "./api/vehicle/vehicle-repair.repo";
import {VehicleRepairDuckDb} from "./duckdb/vehicle-repair.duckdb";
import {VehicleTireRepo} from "./api/vehicle/vehicle-tire.repo";
import {VehicleTireDuckDb} from "./duckdb/vehicle-tire.duckdb";

type RepoMappings<T> = Record<string, Type<T>>;

@Injectable({providedIn: 'root'})
export class RepoProvider {

  private readonly injector: Injector = inject(Injector);
  private readonly log: LoggingService = inject(LoggingService);

  private readonly repoMode: string = environment.repository;
  private readonly repoMappings: Map<Type<any>, RepoMappings<any>> = new Map<Type<any>, RepoMappings<any>>();

  constructor() {
    this.initDefaultMappings();
  }

  public getRepo<T>(repoType: Type<T>): T {
    const mappings: RepoMappings<any> | undefined = this.repoMappings.get(repoType);
    if (notDefined(mappings)) {
      return this.getObject(repoType);
    }

    const targetRepoType: Type<any> = mappings[this.repoMode];
    if (notDefined(targetRepoType)) {
      this.log.error('Mapping for "{}" in mode "{}" not found. Fallback to default repository', [repoType.name, this.repoMode]);
      return this.getObject(repoType);
    }
    return this.getObject(targetRepoType);
  }

  private getObject<T>(type: Type<T>): T {
    return this.injector.get(type, undefined, {optional: false});
  }

  private initDefaultMappings(): void {
    this.registerRepoMappings(UserRepo, {'api': UserRepo, 'duckdb': UserDuckDb});
    this.registerRepoMappings(InfoRepo, {'api': InfoRepo, 'duckdb': InfoDuckDb});
    this.registerRepoMappings(I18nRepo, {'api': I18nRepo, 'duckdb': I18nDuckDb});
    this.registerRepoMappings(OrgRepo, {'api': OrgRepo, 'duckdb': OrgDuckDb});
    this.registerRepoMappings(OverviewRepo, {'api': OverviewRepo, 'duckdb': OverviewDuckDb});
    this.registerRepoMappings(NotificationRepo, {'api': NotificationRepo, 'duckdb': NotificationDuckDb});
    this.registerRepoMappings(VatRateRepo, {'api': VatRateRepo, 'duckdb': VatRateDuckDb});
    this.registerRepoMappings(CurrencyRepo, {'api': CurrencyRepo, 'duckdb': CurrencyDuckDb});
    this.registerRepoMappings(InvoiceRepo, {'api': InvoiceRepo, 'duckdb': InvoiceDuckDb});
    this.registerRepoMappings(ContactRepo, {'api': ContactRepo, 'duckdb': ContactDuckDb});
    this.registerRepoMappings(ReceiptRepo, {'api': ReceiptRepo, 'duckdb': ReceiptDuckDb});
    this.registerRepoMappings(ArticleRepo, {'api': ArticleRepo, 'duckdb': ArticleDuckDb});
    this.registerRepoMappings(ContactVehicleRepo, {'api': ContactVehicleRepo, 'duckdb': ContactVehicleDuckDb});
    this.registerRepoMappings(VehicleRepo, {'api': VehicleRepo, 'duckdb': VehicleDuckDb});
    this.registerRepoMappings(MediaRepo, {'api': MediaRepo, 'duckdb': MediaDuckDb});
    this.registerRepoMappings(ContactRelationRepo, {'api': ContactRelationRepo, 'duckdb': ContactRelationDuckdb});
    this.registerRepoMappings(DeathRepo, {'api': DeathRepo, 'duckdb': DeathDuckDb});
    this.registerRepoMappings(RegistryOfficeRepo, {'api': RegistryOfficeRepo, 'duckdb': RegistryOfficeDuckDb});
    this.registerRepoMappings(GoodsGroupRepo, {'api': GoodsGroupRepo, 'duckdb': GoodsGroupDuckDb});
    this.registerRepoMappings(VehicleHistoryRepo, {'api': VehicleHistoryRepo, 'duckdb': VehicleHistoryDuckDb});
    this.registerRepoMappings(VehicleRepairRepo, {'api': VehicleRepairRepo, 'duckdb': VehicleRepairDuckDb});
    this.registerRepoMappings(VehicleTireRepo, {'api': VehicleTireRepo, 'duckdb': VehicleTireDuckDb});
  }

  private registerRepoMappings<T>(baseRepoType: Type<T>, mappings: RepoMappings<T>): void {
    this.repoMappings.set(baseRepoType, mappings);
  }
}
